/*
  Koolseed — kusursuz marquee döngüsü.

  CSS-only marquee'lerde klasik yöntem içeriği iki kez basıp `translateX(-50%)`
  uygulamaktır. Bu yalnızca tek bir kopya ekran genişliğinden GENİŞSE dikişsiz
  görünür; dar kalırsa döngünün bir yerinde boşluk açılır. Tasarımda şerit
  hiçbir anda boş görünmediği için burada ölçüm yapıp yeterli sayıda kopya
  üretiyoruz:

    - ilk çocuk "usta" kopya kabul edilir, fazlalıklar temizlenir,
    - toplam genişlik konteynerin 2 katını geçene kadar klonlanır (en az 2),
    - kaydırma mesafesi tam olarak BİR kopya genişliği (--ks-mq-w) olur.

  Mesafe her zaman bir kopya olduğu için hız, klon sayısından bağımsız:
  yazarın verdiği süre (`--mq-speed`) aynen korunur.

  İkinci mod: `data-ks-marquee-drag`. Burada CSS animasyonu yerine gerçek bir
  kaydırma kutusu var; akış scrollLeft'i her karede ilerleterek yapılıyor.
  Böylece şerit hem kendi kendine akıyor hem de parmakla/fareyle
  kaydırılabiliyor. Bir kopya genişliği kadar ilerleyince aynı miktar geri
  alınıyor — kopyalar birebir aynı olduğu için dikiş görünmüyor.

  Akış; imleç şeridin üstündeyken, kullanıcı elle kaydırırken ve şerit içinde
  bir video oynarken duruyor.
*/
(() => {
  const READY = 'is-marquee-ready';

  function layout(root) {
    const track = root.querySelector('[data-ks-marquee-track]');
    if (!track) return;

    // Fazla klonları at, ustayı bırak.
    const sets = track.querySelectorAll('[data-ks-marquee-set]');
    for (let i = sets.length - 1; i > 0; i--) sets[i].remove();
    const master = track.firstElementChild;
    if (!master) return;

    root.classList.remove(READY);
    track.style.removeProperty('--ks-mq-w');

    // Ölçüm: kesirli piksel önemli, getBoundingClientRect kullanıyoruz.
    const setWidth = master.getBoundingClientRect().width;
    if (!setWidth) return;

    // Sürüklenebilir modda iki yöne de yer gerekiyor: bir kopya fazlası.
    const drag = root.hasAttribute('data-ks-marquee-drag');
    const extra = drag ? 2 : 1;
    const need = Math.max(drag ? 3 : 2, Math.ceil((root.getBoundingClientRect().width * 2) / setWidth) + extra);
    const frag = document.createDocumentFragment();
    for (let i = 1; i < need; i++) {
      const clone = master.cloneNode(true);
      clone.setAttribute('aria-hidden', 'true');
      clone.querySelectorAll('a, button, input').forEach((el) => el.setAttribute('tabindex', '-1'));
      frag.appendChild(clone);
    }
    track.appendChild(frag);

    track.style.setProperty('--ks-mq-w', setWidth + 'px');
    root.classList.add(READY);

    // Taşma yoksa (masaüstünde ızgaraya dönen şeritler) akışa gerek yok.
    if (drag && root.scrollWidth > root.clientWidth + 1) startAutoScroll(root, setWidth);
    else if (root._ksStop) root._ksStop();
  }

  /* ---- Sürüklenebilir akış ------------------------------------------------ */

  function startAutoScroll(root, setWidth) {
    if (root._ksStop) root._ksStop();

    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const seconds = Number(root.dataset.ksMarqueeSpeed) || 45;
    const direction = root.dataset.ksMarqueeDirection === 'right' ? -1 : 1;
    const pxPerSecond = setWidth / seconds;

    // Başlangıçta bir kopya kadar içeride dur: iki yöne de kaydırma payı olsun.
    root.scrollLeft = setWidth;

    let raf = 0;
    let last = 0;
    let idle = 0;
    let hovering = false;
    let interacting = false;

    const paused = () => reduced || hovering || interacting || root._ksPlaying > 0;

    const pause = () => {
      interacting = true;
      window.clearTimeout(idle);
      idle = window.setTimeout(() => { interacting = false; }, 1400);
    };

    const wrap = () => {
      // Kopyalar aynı olduğu için tam bir kopya kadar sıçramak görünmez.
      if (root.scrollLeft >= setWidth * 2) root.scrollLeft -= setWidth;
      else if (root.scrollLeft <= 0) root.scrollLeft += setWidth;
    };

    const tick = (now) => {
      raf = window.requestAnimationFrame(tick);
      if (!last) { last = now; return; }
      const delta = now - last;
      last = now;
      if (paused()) { wrap(); return; }
      // Sekme arkaplandayken delta çok büyür; sıçramayı engelle.
      root.scrollLeft += direction * pxPerSecond * Math.min(delta, 50) / 1000;
      wrap();
    };
    raf = window.requestAnimationFrame(tick);

    // Kullanıcı dokunduğunda dursun, bıraktıktan kısa süre sonra devam etsin.
    const onPointerDown = (event) => {
      pause();
      if (event.pointerType !== 'mouse') return; // dokunmatikte tarayıcı hallediyor
      const startX = event.clientX;
      const startScroll = root.scrollLeft;
      root.classList.add('is-dragging');

      const move = (moveEvent) => {
        root.scrollLeft = startScroll - (moveEvent.clientX - startX);
        wrap();
      };
      const up = () => {
        root.classList.remove('is-dragging');
        window.removeEventListener('pointermove', move);
        window.removeEventListener('pointerup', up);
        pause();
      };
      window.addEventListener('pointermove', move);
      window.addEventListener('pointerup', up);
    };

    root.addEventListener('pointerdown', onPointerDown);
    root.addEventListener('wheel', pause, { passive: true });
    root.addEventListener('touchstart', pause, { passive: true });
    root.addEventListener('mouseenter', () => { hovering = true; });
    root.addEventListener('mouseleave', () => { hovering = false; });

    root._ksStop = () => {
      window.cancelAnimationFrame(raf);
      window.clearTimeout(idle);
      root.removeEventListener('pointerdown', onPointerDown);
      root._ksStop = null;
    };
  }

  /* Video oynarken şerit durur — oynayan kart ekrandan kayıp gitmesin.
     Medya olayları köpürmediği için yakalama aşamasında dinleniyor. Sayaç
     kökte tutuluyor: akış her yeniden ölçümde baştan kuruluyor, dinleyiciler
     orada olsa üst üste birikirdi. */
  function watchMedia(root) {
    root._ksPlaying = 0;
    const down = () => { root._ksPlaying = Math.max(0, root._ksPlaying - 1); };
    root.addEventListener('play', () => { root._ksPlaying += 1; }, true);
    root.addEventListener('pause', down, true);
    root.addEventListener('ended', down, true);
  }

  function init(root) {
    if (root.dataset.ksMarqueeInit) return;
    root.dataset.ksMarqueeInit = '1';
    watchMedia(root);

    const run = () => layout(root);

    // Yazı tipleri geç yüklenirse genişlik değişir — ölçümü tekrarla.
    if (document.fonts && document.fonts.ready) document.fonts.ready.then(run);

    if ('ResizeObserver' in window) {
      let raf = 0;
      const ro = new ResizeObserver(() => {
        cancelAnimationFrame(raf);
        raf = requestAnimationFrame(run);
      });
      ro.observe(root);
    } else {
      window.addEventListener('resize', run, { passive: true });
    }

    run();
  }

  function scan(scope) {
    (scope || document).querySelectorAll('[data-ks-marquee]').forEach(init);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => scan());
  } else {
    scan();
  }

  // Theme editor bölüm yeniden yüklediğinde tekrar kur.
  document.addEventListener('shopify:section:load', (e) => scan(e.target));
  document.addEventListener('shopify:section:unload', (e) => {
    e.target.querySelectorAll('[data-ks-marquee]').forEach((r) => delete r.dataset.ksMarqueeInit);
  });
})();
